import { Helmet } from 'react-helmet'
import { Onboarding } from '@/onboarding/layout/OnboardingPageTemplate'
import { useNavigate } from 'react-router'
import { useEffect, useRef } from 'react'
import { AxiosError } from 'axios'
import { toast } from 'sonner'
import { APP_ROUTES } from '@/common/constants'
import { APP, Errors } from '@/common/strings'
import { ONBOARDING_COPY } from '@/locales/en'
import { FullScreenLoader } from '@/common/ui/FullScreenLoader'
import useUserStore from '../userStore'
import useAccountStore from '@/account/accountStore'
import { useCurrentAccount } from '@/account/useCurrentAccount'
import { useOnboardingProgress } from '@/onboarding/useOnboardingProgress'
import { useOnboardingFlow } from '@/onboarding/useOnboardingFlow'
import { analytics } from '@/common/analytics/analytics'
import { useTermsAndConditionsFlow } from './useTermsAndConditionsFlow'
import { useTermsAndConditionsSections } from './useTermsAndConditionsSections'
import { SectionedTermsAndConditionsContent } from './SectionedTermsAndConditionsContent'

function getNextRouteByFlowType(flowType: string): string {
    switch (flowType) {
        case 'new-account':
            return APP_ROUTES.KYC_INIT
        case 'invite':
            return APP_ROUTES.ACCOUNT_CREATED
        default:
            return APP_ROUTES.KYC_INIT
    }
}

/** Production page — wires useTermsAndConditionsFlow result to UI.
 *  Navigation guards and analytics are kept here (not in the hook) since they are route concerns, not business logic.
 */
export function TermsAndConditions() {
    const navigate = useNavigate()
    const { progress } = useOnboardingProgress()
    const { flowType } = useOnboardingFlow()
    const { user } = useUserStore()
    const account = useCurrentAccount()
    const showErrorToastRef = useRef(false)

    const { policiesResponse, policiesLoading, policiesError, onSubmit, isSuccess, error, isAccepting } =
        useTermsAndConditionsFlow(account?.accountId)
    const { sections, isCheckboxChecked, toggleCheckbox, allRequiredChecked } = useTermsAndConditionsSections(
        policiesResponse?.policies,
    )

    // Guard: Skip form if terms already accepted — only for the invite flow, where an
    // adviser-created account may already have terms accepted before the client ever
    // reaches this page. New-account signups must always see and accept the form.
    useEffect(() => {
        if (flowType === 'invite' && policiesResponse?.termsAccepted) {
            navigate(getNextRouteByFlowType(flowType))
        }
    }, [policiesResponse?.termsAccepted, flowType, navigate])

    // Guard: On successful submission → track analytics + navigate
    useEffect(() => {
        if (!isSuccess) return

        const versionMap = policiesResponse?.policies.reduce(
            (acc, policy) => {
                acc[policy.slug] = policy.versionId
                return acc
            },
            {} as Record<string, string>,
        )

        analytics.trackWithDefaults('onboarding_tac_submitted', {
            ...versionMap,
        })
        navigate(getNextRouteByFlowType(flowType))
    }, [isSuccess, flowType, navigate, policiesResponse?.policies])

    // Guard: Handle submission errors (excluding 409 "already accepted")
    useEffect(() => {
        if (!error || showErrorToastRef.current) return

        const axiosError = error as AxiosError
        const is409 = axiosError?.response?.status === 409

        if (is409) {
            if (account?.accountId) {
                useAccountStore.getState().confirmTermsAccepted(account.accountId)
            }
            navigate(getNextRouteByFlowType(flowType))
            return
        }

        const versionMap = policiesResponse?.policies.reduce(
            (acc, policy) => {
                acc[policy.slug] = policy.versionId
                return acc
            },
            {} as Record<string, string>,
        )

        analytics.trackWithDefaults('onboarding_tac_failed', {
            ...versionMap,
        })
        toast.error(Errors.server)
        showErrorToastRef.current = true
    }, [account?.accountId, error, flowType, navigate, policiesResponse?.policies])

    // Guard: Handle policy fetch errors
    useEffect(() => {
        if (!policiesError || showErrorToastRef.current) return

        analytics.trackWithDefaults('onboarding_tac_query_failed', {
            error: 'Failed to fetch account policies',
        })
        toast.error(Errors.unknown)
        showErrorToastRef.current = true
    }, [policiesError])

    if (!user || !account?.accountId || policiesLoading || !policiesResponse?.policies.length) {
        return <FullScreenLoader dark />
    }

    return (
        <>
            <Helmet>
                <title>
                    {ONBOARDING_COPY.termsAndConditions.pageTitle} | {APP.title}
                </title>
            </Helmet>
            <Onboarding
                title={ONBOARDING_COPY.termsAndConditions.title}
                description={ONBOARDING_COPY.termsAndConditions.description}
                cta={ONBOARDING_COPY.termsAndConditions.primaryButton}
                onSubmit={onSubmit}
                onBack={() => navigate(APP_ROUTES.ACCOUNT_TYPE)}
                progress={progress}
                isLoading={isAccepting}
                disabled={!allRequiredChecked}
            >
                <SectionedTermsAndConditionsContent
                    sections={sections}
                    isCheckboxChecked={isCheckboxChecked}
                    onToggleCheckbox={toggleCheckbox}
                />
            </Onboarding>
        </>
    )
}
